import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/common";
import { toast } from "sonner";
import { CheckCircle2, HelpCircle } from "lucide-react";

export default function SharePage() {
  const { token } = useParams();
  const [answers, setAnswers] = useState({});
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["share", token],
    queryFn: async () => (await api.get(`/share/${token}`)).data,
  });

  const updateTask = async (taskId, status) => {
    const fd = new FormData();
    fd.append("task_id", taskId); fd.append("status", status);
    await api.post(`/share/${token}/task-status`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Action item updated");
    refetch();
  };

  const submit = async (item) => {
    const val = answers[item.id];
    if (!val) { toast.error("Please provide an answer"); return; }
    try {
      await api.post(`/share/${token}/answer`, { question_id: item.id, answer: val });
      toast.success("Answer saved — thank you!");
      refetch();
    } catch { toast.error("Could not save answer"); }
  };

  if (isLoading) return <div className="min-h-screen grid place-items-center bg-[#f2f6fb]"><Spinner /></div>;
  if (!data) return <div className="min-h-screen grid place-items-center bg-[#f2f6fb] text-slate-600">This link is invalid or has expired.</div>;

  return (
    <div className="min-h-screen bg-[#f2f6fb]">
      <div className="tri-rule" />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Logo onLight />
        </div>
        <div className="bg-white rounded-xl border border-[#e1e8f0] p-6 shadow-sm">
          <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase">Stakeholder Questionnaire</div>
          <h1 className="text-2xl font-bold text-[#0f2557] mt-1">{data.meeting_title}</h1>
          <p className="text-sm text-[#5c6b84] mt-1">{data.client} · Your answers feed directly into the project deliverables.</p>
        </div>

        <div className="space-y-4 mt-5">
          {data.items.map((q) => {
            const answered = q.status === "answered" || q.status === "Answered" || q.answer;
            const options = q.options || [];
            return (
              <div key={q.id} className="bg-white rounded-xl border border-[#e1e8f0] p-5" data-testid={`share-q-${q.id}`}>
                <div className="flex items-start gap-2">
                  <HelpCircle size={18} className="text-[#29abe2] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-[#25304a]">{q.question}</div>
                    {q.why_it_matters && <div className="text-xs text-[#5c6b84] mt-1">{q.why_it_matters}</div>}
                    {q.module && <span className="inline-block text-[10px] bg-[#f2f6fb] text-[#5c6b84] px-2 py-0.5 rounded mt-2">{q.module}</span>}
                  </div>
                  {answered && <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />}
                </div>
                <div className="mt-3 pl-7">
                  {options.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {options.map((o) => (
                        <button key={o} onClick={() => setAnswers({ ...answers, [q.id]: o })}
                          className={`text-sm px-3 py-1.5 rounded-lg border transition ${answers[q.id] === o ? "bg-[#0f2557] text-white border-[#0f2557]" : "bg-white text-[#25304a] border-[#e1e8f0] hover:border-[#29abe2]"}`}>
                          {o}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <Textarea placeholder="Your answer…" value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
                  )}
                  {answers[q.id] && options.includes("Other") && answers[q.id] === "Other" && (
                    <Input className="mt-2" placeholder="Please specify…" onChange={(e) => setAnswers({ ...answers, [q.id]: "Other: " + e.target.value })} />
                  )}
                  <Button size="sm" className="mt-3 bg-[#0f2557] hover:bg-[#163a7a]" onClick={() => submit(q)} data-testid={`share-submit-${q.id}`}>
                    {answered ? "Update answer" : "Submit answer"}
                  </Button>
                  {answered && <span className="text-xs text-emerald-600 ml-3">Saved: {q.answer}</span>}
                </div>
              </div>
            );
          })}
        </div>
        {data.kind === "portal" && (data.tasks || []).length > 0 && (
          <div className="bg-white rounded-xl border border-[#e1e8f0] p-5 mt-5" data-testid="portal-tasks">
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-3">Your Action Items</div>
            <div className="space-y-2">
              {data.tasks.map((t) => (
                <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#e1e8f0]" data-testid={`portal-task-${t.id}`}>
                  <div className="flex-1">
                    <div className={`text-sm text-[#25304a] ${t.status === "done" ? "line-through opacity-60" : ""}`}>{t.title}</div>
                    {t.owner && <div className="text-xs text-[#5c6b84]">Owner: {t.owner}</div>}
                  </div>
                  <select value={t.status} onChange={(e) => updateTask(t.id, e.target.value)}
                    className="text-sm border border-[#e1e8f0] rounded-md h-9 px-2 bg-white text-[#25304a]">
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="done">Done</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="text-center text-xs text-[#5c6b84] mt-8 py-4">
          Yanthra Labs Private Limited · Chennai · PRECISION · CARE · CONTINUITY
        </div>
      </div>
    </div>
  );
}
